import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';

import NoDataAvailable from '../no-data-available/NoDataAvailable';
import './custom-heatmap-chart.scss';

/**
 * Represents a single row in the heatmap chart.
 */
export interface HeatmapDataType {
  name: string;
  data: number[];
}

interface CustomHeatmapChartProperties {
  series: HeatmapDataType[];
  xaxisLabels?: string[];
  height?: number | string;
  colors?: {
    low: string;
    high: string;
  };
  valueUnit?: string;
}

/**
 * CustomHeatmapChart - A shared heatmap component using ApexCharts.
 * @param {any} props - Component properties.
 * @param {any} props.series - The series prop
 * @param {any} props.xaxisLabels - The xaxisLabels prop
 * @param {any} props.height - The height prop
 * @param {any} props.colors - The colors prop
 * @param {any} props.valueUnit - The valueUnit prop
 * @returns {any} The rendered heatmap chart.
 */
function CustomHeatmapChart({
  series,
  xaxisLabels,
  height = 350,
  colors = { low: '#2E9E3A', high: '#F04438' },
  valueUnit = 'kW',
}: CustomHeatmapChartProperties) {
  const isEmpty = series.length === 0 || series.every((s) => s.data.every((v) => v === 0 || !v));

  const chartOptions = useMemo(() => {
    if (isEmpty) return {};
    // Calculate median for color scale
    const allValues = series
      .flatMap((row) => row.data)
      .filter((value) => typeof value === 'number' && !Number.isNaN(value) && value !== 0);

    let roundedMedian = 0;
    let maxValue = 0;

    if (allValues.length > 0) {
      const sorted = [...allValues].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
      roundedMedian = Math.round(median);
      maxValue = Math.max(...allValues);
    }

    const options: ApexCharts.ApexOptions = {
      chart: {
        type: 'heatmap',
        toolbar: { show: false },
        fontFamily: 'Inter, sans-serif',
      },
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.5,
          radius: 6,
          useFillColorAsStroke: true,
          colorScale: {
            ranges: [
              {
                from: 0,
                to: roundedMedian,
                name: 'Low',
                color: colors.low,
              },
              {
                from: roundedMedian,
                to: maxValue || roundedMedian * 2 || 100,
                name: 'High',
                color: colors.high,
              },
            ],
          },
        },
      },
      dataLabels: { enabled: false },
      legend: {
        position: 'bottom',
        fontSize: '12px',
        fontWeight: 500,
        labels: {
          colors: '#64748B',
        },
      },
      xaxis: {
        categories: xaxisLabels,
        labels: {
          style: {
            colors: '#64748B',
            fontSize: '11px',
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: '#64748B',
            fontSize: '11px',
          },
        },
      },
      tooltip: {
        /**
         * Custom tooltip renderer for heatmap cells.
         * @param {any} param0 - The chart's series data.
         * @param {any} param0.series - The series prop
         * @param {any} param0.seriesIndex - The seriesIndex prop
         * @param {any} param0.dataPointIndex - The dataPointIndex prop
         * @returns {any} HTML string to be rendered inside the tooltip.
         */
        custom({ series: s, seriesIndex, dataPointIndex }): string {
          const value = s[seriesIndex][dataPointIndex];
          const displayValue = value;

          return `
            <div style="padding: 8px; font-size: 13px; background: #fff; border: 1px solid #E2E8F0; border-radius: 6px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);">
              ${displayValue} ${valueUnit}
            </div>
          `;
        },
      },
    };

    return options;
  }, [series, colors, valueUnit, xaxisLabels, isEmpty]);

  return (
    <div className="custom-heatmap-chart-wrap" style={{ height }}>
      {isEmpty ? (
        <NoDataAvailable />
      ) : (
        <Chart options={chartOptions} series={series} type="heatmap" height="100%" />
      )}
    </div>
  );
}

CustomHeatmapChart.defaultProps = {
  xaxisLabels: [],
  height: 350,
  colors: { low: '#2E9E3A', high: '#F04438' },
  valueUnit: 'kW',
};

export default CustomHeatmapChart;
